import { useMsal } from '@azure/msal-react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Avatar,
  Button,
  Divider,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';

import logo from 'innovate/assets/images/abi_logo.png';
import userImg from 'innovate/assets/images/admin.svg';
import Group from 'innovate/assets/images/Group.svg';
import InnovateX from 'innovate/assets/images/logosSets/LogoWide/PNG/LogoWideYellowWhite.png';
import logOut from 'innovate/assets/images/Logout.svg';
import manageAccounts from 'innovate/assets/images/manage_accounts.svg';
import { ApprovalContext } from 'innovate/components/ApprovalProvider';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { STORAGE_KEYS } from 'innovate/utils/Constants';

const SESSION_KEYS_TO_CLEAR = [
  STORAGE_KEYS.COUNTRY,
  STORAGE_KEYS.MARKET_STRUCTURE,
  STORAGE_KEYS.FILTERED_DATA_STRUCTURE,
  STORAGE_KEYS.EXPANDED_KEYS,
  STORAGE_KEYS.PACK_EXPANDED_SEGMENTS,
  STORAGE_KEYS.PACK_EXPANDED_MANUFACTURERS,
  STORAGE_KEYS.PACK_TOTAL_MARKET_EXPANDED,
  STORAGE_KEYS.PACK_COUNTRY,
];

const clearSessionKeys = () =>
  SESSION_KEYS_TO_CLEAR.forEach((key) => sessionStorage.removeItem(key));

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { userName, userType, profilePhoto } = useContext(ApprovalContext);
  const bgColor = useColorModeValue('black', 'black.900');

  const currentPath = location.pathname;
  const isMarketView = currentPath === '/pack-price/market-view';

  // Clear session keys on route change (except market view)
  useEffect(() => {
    if (!isMarketView) {
      clearSessionKeys();
    }
  }, [location.pathname, isMarketView]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Clear session keys on page unload — registered once with cleanup
  useEffect(() => {
    window.addEventListener('beforeunload', clearSessionKeys);
    return () => {
      window.removeEventListener('beforeunload', clearSessionKeys);
    };
  }, []);

  const isActive = useCallback(
    (paths: string) => {
      const pathArray = paths.split(',');
      return pathArray.includes(currentPath)
        ? {
            textDecoration: 'underline',
            textDecorationThickness: '2px',
            textUnderlineOffset: '5px',
            color: '#FFFFFF',
          }
        : { color: '#848484' };
    },
    [currentPath]
  );

  const logout = useCallback(() => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
      mainWindowRedirectUri: '/',
    });
  }, [instance]);

  const navigateToAccess = useCallback(() => {
    navigate('/access', { state: { updateAccess: true } });
  }, [navigate]);

  const navigateToAdmin = useCallback(() => {
    navigate('/admin', { state: { updateAccess: true } });
  }, [navigate]);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Flex
      position="sticky"
      zIndex={9999}
      top="0"
      borderBottom="solid 1px"
      borderColor="border"
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      padding="10px 24px"
      bg={bgColor}
    >
      <Flex align="center" columnGap="30px">
        <Link to="/">
          <Image w="106px" src={logo} alt="loginFootLogo" />
        </Link>

        <Link to="/">
          <Image w="110px" src={InnovateX} alt="loginFootLogo" />
        </Link>
      </Flex>
      <Flex align="center" columnGap="80px" fontWeight="bold">
        <NavLink to="/" style={isActive('/')}>
          Home
        </NavLink>
        <NavLink to="/pack-price" style={isActive('/pack-price,/pack-price/market-view,')}>
          Pack Price
        </NavLink>
        <NavLink
          to="/pre-launch"
          style={isActive(
            '/pre-launch,/pre-launch/compare,/pre-launch/new-simulation,/pre-launch/edit-simulation'
          )}
        >
          Pre Launch
        </NavLink>

        <NavLink
          to="/post-launch/new-design"
          style={isActive('/post-launch,/post-launch/new-design')}
        >
          Post Launch
        </NavLink>
      </Flex>
      <Flex alignItems="center" as="nav" columnGap="12px">
        <Menu onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)}>
          <MenuButton
            as={Button}
            bg="none"
            rightIcon={
              isMenuOpen ? <ChevronUpIcon color="white" /> : <ChevronDownIcon color="white" />
            }
          >
            <Flex align="center" gap={2}>
              {profilePhoto ? (
                <Avatar size="sm" src={profilePhoto} bg="#E3B100" />
              ) : (
                <FaUserCircle size={24} color="white" />
              )}
              <Text color="white">{userName}</Text>
            </Flex>
          </MenuButton>

          <MenuList>
            <MenuItem
              icon={<img src={userImg} alt="userType" />}
              _hover={{ bg: 'transparent' }}
              _focus={{ bg: 'transparent' }}
              cursor="default"
              mb={2}
            >
              <Flex direction="column">
                <Text color="#8080809e" fontWeight={600} fontSize="14px" textTransform="capitalize">
                  {userType}
                </Text>
              </Flex>
            </MenuItem>

            <Divider />
            {userType === 'admin' && (
              <MenuItem
                _hover={{ bg: '#dedede' }}
                _focus={{ bg: '#dedede' }}
                icon={<img src={manageAccounts} alt="Admin" />}
                onClick={navigateToAdmin}
                my={2}
              >
                <Flex direction="column">
                  <Text fontWeight="600" fontSize="14px">
                    Go To Admin
                  </Text>
                </Flex>
              </MenuItem>
            )}
            <Tooltip label="Request new or edit existing access" placement="bottom">
              <MenuItem
                _hover={{ bg: '#dedede' }}
                _focus={{ bg: '#dedede' }}
                icon={<img src={Group} alt="Group" />}
                onClick={navigateToAccess}
                my={2}
              >
                <Flex direction="column">
                  <Text fontWeight={600} fontSize="14px">
                    Raise New/Edit Access
                  </Text>
                </Flex>
              </MenuItem>
            </Tooltip>
            <MenuItem
              icon={<img src={logOut} alt="logout" />}
              mb={2}
              _hover={{ bg: '#dedede' }}
              _focus={{ bg: '#dedede' }}
              onClick={logout}
            >
              <Flex direction="column">
                <Text fontWeight={600} fontSize="14px">
                  Log Out
                </Text>
              </Flex>
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </Flex>
  );
};

export default Header;
